import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTable, usePagination } from 'react-table';
import Api from '../Auth/Api';
import { Modal, Button, Toast, Tab, Tabs, Form, Spinner } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';

function Termos() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    
    const [termos, setTermos] = useState([]);
    const [termoAtual, setTermoAtual] = useState(null);
    const [aceites, setAceites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingAceites, setLoadingAceites] = useState(false);
    const [showModalUpload, setShowModalUpload] = useState(false);
    const [showModalVisualizar, setShowModalVisualizar] = useState(false);
    const [termoVisualizar, setTermoVisualizar] = useState(null);
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [fileSelected, setFileSelected] = useState(false);
    const [uploading, setUploading] = useState(false);
    
    const fileInputRef = useRef(null);
    const { handleSubmit, control, reset, formState: { errors } } = useForm();
    
    // Filtros para aceites
    const [filtroSearch, setFiltroSearch] = useState('');
    const [filtroTermo, setFiltroTermo] = useState('');
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');
    
    // Paginação server-side
    const [pageCount, setPageCount] = useState(0);
    const [totalAceites, setTotalAceites] = useState(0);

    // Função para construir URL do arquivo
    const getFileUrl = useCallback((arquivo) => {
        if (!arquivo) return null;
        // O arquivo pode vir em dois formatos:
        // 1. nome_gerado/nome_original.pdf (formato completo)
        // 2. nome_gerado.pdf (apenas nome gerado com extensão)
        // Precisamos extrair apenas o nome do diretório (sem extensão)
        let diretorio;
        if (arquivo.includes('/')) {
            // Formato: nome_gerado/nome_original.pdf
            diretorio = arquivo.split('/')[0];
        } else {
            // Formato: nome_gerado.pdf - remover extensão
            diretorio = arquivo.replace(/\.pdf$/i, '');
        }
        const base = (process.env.REACT_APP_FILES_URL || '').replace(/\/$/, '');
        return `${base}/${diretorio}`;
    }, []);

    // Carregar termos
    useEffect(() => {
        async function fetchTermos() {
            try {
                const response = await Api.get('api/termos', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setTermos(response.data);
                
                // Encontrar termo ativo
                const ativo = response.data.find(t => t.ativo === 1);
                setTermoAtual(ativo || null);
            } catch (error) {
                console.error('Erro ao carregar termos:', error);
                setToast({ header: 'Erro!', body: error.response?.data?.message || 'Erro ao carregar termos', type: 'toast-error' });
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        }
        fetchTermos();
    }, [token]);

    // Carregar aceites com filtros e paginação
    const fetchAceites = useCallback(async (pageIndex = 0, pageSize = 20) => {
        setLoadingAceites(true);
        try {
            const params = {
                page: pageIndex + 1,
                limit: pageSize
            };
            if (filtroSearch) params.search = filtroSearch;
            if (filtroTermo) params.id_termo = filtroTermo;
            if (filtroDataInicio) params.data_inicio = filtroDataInicio;
            if (filtroDataFim) params.data_fim = filtroDataFim;
            
            const response = await Api.get('api/termos-aceite', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            setAceites(response.data.data || []);
            setPageCount(response.data.totalPages || 0);
            setTotalAceites(response.data.total || 0);
        } catch (error) {
            console.error('Erro ao carregar aceites:', error);
            setToast({ header: 'Erro!', body: error.response?.data?.message || 'Erro ao carregar histórico de aceites', type: 'toast-error' });
            setShowToast(true);
        } finally {
            setLoadingAceites(false);
        }
    }, [token, filtroSearch, filtroTermo, filtroDataInicio, filtroDataFim]);

    // Upload de novo termo
    const onSubmitUpload = async (data) => {
        if (!data.arquivo || !(data.arquivo instanceof FileList) || data.arquivo.length === 0) {
            setToast({ header: 'Erro!', body: 'Selecione um arquivo PDF', type: 'toast-error' });
            setShowToast(true);
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('arquivo', data.arquivo[0]);

            const response = await Api.post('api/termo/upload', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            setShowToast(true);
            setShowModalUpload(false);
            reset();
            setFileSelected(false);
            
            // Recarregar termos
            const termosResponse = await Api.get('api/termos', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTermos(termosResponse.data);
            const ativo = termosResponse.data.find(t => t.ativo === 1);
            setTermoAtual(ativo || null);
        } catch (error) {
            setToast({ header: 'Erro!', body: error.response?.data?.message || 'Erro ao fazer upload do termo', type: 'toast-error' });
            setShowToast(true);
        } finally {
            setUploading(false);
        }
    };

    // Visualizar termo
    const handleVisualizarTermo = (termo) => {
        setTermoVisualizar(termo);
        setShowModalVisualizar(true);
    };

    // Formatar data
    const formatarData = (dataString) => {
        if (!dataString) return '-';
        const data = new Date(dataString);
        return data.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Colunas da tabela de versões
    const colunasVersoes = useMemo(() => [
        {
            Header: 'Arquivo',
            accessor: 'arquivo',
            Cell: ({ value }) => {
                // Extrair o nome do arquivo do caminho completo
                // Formato esperado: nome_gerado/nome_original.pdf
                let nomeArquivo;
                if (value && value.includes('/')) {
                    // Formato: nome_gerado/nome_original.pdf
                    nomeArquivo = value.split('/')[1];
                } else if (value) {
                    // Se não tem barra, pode ser um formato antigo ou nome gerado
                    // Se o nome parece ser gerado (muito longo, alfanumérico aleatório)
                    // Mostrar como "termo.pdf"
                    const nomeSemExtensao = value.replace(/\.pdf$/i, '');
                    // Nomes gerados geralmente têm mais de 20 caracteres e são alfanuméricos sem espaços
                    if (nomeSemExtensao.length > 20 && /^[a-zA-Z0-9]+$/.test(nomeSemExtensao)) {
                        nomeArquivo = 'termo.pdf';
                    } else {
                        // Caso contrário, mostrar o valor original
                        nomeArquivo = value;
                    }
                } else {
                    nomeArquivo = value || '-';
                }
                return (
                    <span className="text-primary fw-semibold">{nomeArquivo}</span>
                );
            }
        },
        {
            Header: 'Data Criação',
            accessor: 'data_criacao',
            Cell: ({ value }) => formatarData(value)
        },
        {
            Header: 'Criado Por',
            accessor: 'criado_por_nome',
            Cell: ({ value }) => value || '-'
        },
        {
            Header: 'Status',
            accessor: 'ativo',
            Cell: ({ value }) => (
                <span className={`badge ${value === 1 ? 'bg-success' : 'bg-secondary'}`}>
                    {value === 1 ? 'Ativo' : 'Inativo'}
                </span>
            )
        },
        {
            Header: 'Ações',
            Cell: ({ row }) => (
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleVisualizarTermo(row.original)}
                >
                    <i className="bi bi-eye me-1"></i>
                    Visualizar
                </Button>
            )
        }
    ], []);

    // Função auxiliar para extrair nome do arquivo
    const getNomeArquivo = useCallback((value) => {
        if (!value) return '-';
        if (value.includes('/')) {
            return value.split('/')[1];
        }
        const nomeSemExtensao = value.replace(/\.pdf$/i, '');
        if (nomeSemExtensao.length > 20 && /^[a-zA-Z0-9]+$/.test(nomeSemExtensao)) {
            return 'termo.pdf';
        }
        return value;
    }, []);

    // Colunas da tabela de aceites
    const colunasAceites = useMemo(() => [
        {
            Header: 'Nome',
            accessor: 'nome_completo',
            Cell: ({ value }) => <span className="text-primary fw-semibold">{value}</span>
        },
        {
            Header: 'Matrícula',
            accessor: 'codigo'
        },
        {
            Header: 'Termo Aceito',
            accessor: 'arquivo_termo',
            Cell: ({ value }) => {
                const nomeArquivo = getNomeArquivo(value);
                return <span className="text-muted">{nomeArquivo}</span>;
            }
        },
        {
            Header: 'Data Aceite',
            accessor: 'data_aceite',
            Cell: ({ value }) => formatarData(value)
        }
    ], [getNomeArquivo]);

    const {
        getTableProps: getTablePropsVersoes,
        getTableBodyProps: getTableBodyPropsVersoes,
        headerGroups: headerGroupsVersoes,
        rows: rowsVersoes,
        prepareRow: prepareRowVersoes
    } = useTable({
        columns: colunasVersoes,
        data: termos
    });

    const {
        getTableProps: getTablePropsAceites,
        getTableBodyProps: getTableBodyPropsAceites,
        headerGroups: headerGroupsAceites,
        rows: rowsAceites,
        prepareRow: prepareRowAceites,
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        setPageSize,
        gotoPage,
        state: { pageIndex, pageSize }
    } = useTable(
        {
            columns: colunasAceites,
            data: aceites,
            manualPagination: true,
            pageCount: pageCount,
            initialState: { pageIndex: 0, pageSize: 20 }
        },
        usePagination
    );

    // Resetar para primeira página quando filtros mudarem
    const prevFiltersRef = useRef({});
    useEffect(() => {
        const currentFilters = { filtroSearch, filtroTermo, filtroDataInicio, filtroDataFim };
        const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(currentFilters);
        
        if (filtersChanged) {
            prevFiltersRef.current = currentFilters;
            gotoPage(0);
        }
    }, [filtroSearch, filtroTermo, filtroDataInicio, filtroDataFim, gotoPage]);

    // Buscar dados quando mudar página, tamanho da página ou filtros
    const prevPageRef = useRef(null);
    const prevFiltersForFetchRef = useRef(null);
    const isInitialMount = useRef(true);
    useEffect(() => {
        const pageChanged = prevPageRef.current === null || 
            prevPageRef.current.pageIndex !== pageIndex || 
            prevPageRef.current.pageSize !== pageSize;
        const currentFilters = { filtroSearch, filtroTermo, filtroDataInicio, filtroDataFim };
        const filtersChanged = prevFiltersForFetchRef.current === null || 
            JSON.stringify(prevFiltersForFetchRef.current) !== JSON.stringify(currentFilters);
        
        if (isInitialMount.current || pageChanged || filtersChanged) {
            prevPageRef.current = { pageIndex, pageSize };
            prevFiltersForFetchRef.current = currentFilters;
            isInitialMount.current = false;
            fetchAceites(pageIndex, pageSize);
        }
    }, [pageIndex, pageSize, filtroSearch, filtroTermo, filtroDataInicio, filtroDataFim, fetchAceites]);

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center p-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div className="fade-in w-100 mb-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="text-primary">Gestão de Termos de Aceite</h2>
                </div>

                <Tabs defaultActiveKey="termo-atual" style={{ marginLeft: '10px' }}>
                    <Tab eventKey="termo-atual" title="Termo Atual">
                        <div className="card">
                            <div className="card-body">
                                {termoAtual ? (
                                    <>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="mb-0">Termo Ativo</h5>
                                            <Button
                                                variant="primary"
                                                onClick={() => setShowModalUpload(true)}
                                            >
                                                <i className="bi bi-upload me-2"></i>
                                                Trocar Termo
                                            </Button>
                                        </div>
                                        <div className="mb-3">
                                            <p className="text-muted mb-1">
                                                <strong>Data de Criação:</strong> {formatarData(termoAtual.data_criacao)}
                                            </p>
                                            {termoAtual.criado_por_nome && (
                                                <p className="text-muted mb-3">
                                                    <strong>Criado Por:</strong> {termoAtual.criado_por_nome}
                                                </p>
                                            )}
                                        </div>
                                        <div className="border rounded p-3">
                                            <iframe
                                                src={getFileUrl(termoAtual.arquivo)}
                                                title="Termo Atual"
                                                style={{ width: '100%', height: '600px', border: 'none' }}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center p-5">
                                        <i className="bi bi-file-earmark-text display-1 text-muted"></i>
                                        <p className="text-muted mt-3">Nenhum termo ativo encontrado.</p>
                                        <Button
                                            variant="primary"
                                            onClick={() => setShowModalUpload(true)}
                                        >
                                            <i className="bi bi-upload me-2"></i>
                                            Fazer Upload do Primeiro Termo
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Tab>

                    <Tab eventKey="versoes" title="Histórico de Versões">
                        <div className="card">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table {...getTablePropsVersoes()} className="table table-hover">
                                        <thead>
                                            {headerGroupsVersoes.map(headerGroup => (
                                                <tr {...headerGroup.getHeaderGroupProps()}>
                                                    {headerGroup.headers.map(column => (
                                                        <th {...column.getHeaderProps()}>
                                                            {column.render('Header')}
                                                        </th>
                                                    ))}
                                                </tr>
                                            ))}
                                        </thead>
                                        <tbody {...getTableBodyPropsVersoes()}>
                                            {rowsVersoes.map(row => {
                                                prepareRowVersoes(row);
                                                return (
                                                    <tr {...row.getRowProps()}>
                                                        {row.cells.map(cell => (
                                                            <td {...cell.getCellProps()}>
                                                                {cell.render('Cell')}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {termos.length === 0 && (
                                        <div className="text-center p-5 text-muted">
                                            Nenhum termo encontrado.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Tab>

                    <Tab eventKey="aceites" title="Histórico de Aceites">
                        <div className="card">
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label>Buscar por Nome ou Matrícula</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Nome ou matrícula"
                                                value={filtroSearch}
                                                onChange={(e) => setFiltroSearch(e.target.value)}
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label>Filtrar por Termo</Form.Label>
                                            <Form.Select
                                                value={filtroTermo}
                                                onChange={(e) => setFiltroTermo(e.target.value)}
                                            >
                                                <option value="">Todos</option>
                                                {termos.map(termo => (
                                                    <option key={termo.id} value={termo.id}>
                                                        {getNomeArquivo(termo.arquivo)}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label>Data Início</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={filtroDataInicio}
                                                onChange={(e) => setFiltroDataInicio(e.target.value)}
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label>Data Fim</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={filtroDataFim}
                                                onChange={(e) => setFiltroDataFim(e.target.value)}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                                {loadingAceites ? (
                                    <div className="text-center p-5">
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive">
                                            <table {...getTablePropsAceites()} className="table table-hover">
                                                <thead>
                                                    {headerGroupsAceites.map(headerGroup => (
                                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                                            {headerGroup.headers.map(column => (
                                                                <th {...column.getHeaderProps()}>
                                                                    {column.render('Header')}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </thead>
                                                <tbody {...getTableBodyPropsAceites()}>
                                                    {rowsAceites.map(row => {
                                                        prepareRowAceites(row);
                                                        return (
                                                            <tr {...row.getRowProps()}>
                                                                {row.cells.map(cell => (
                                                                    <td {...cell.getCellProps()}>
                                                                        {cell.render('Cell')}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                            {aceites.length === 0 && !loadingAceites && (
                                                <div className="text-center p-5 text-muted">
                                                    Nenhum aceite encontrado.
                                                </div>
                                            )}
                                        </div>
                                        {/* Paginação */}
                                        <div className="row mt-4">
                                            <div className="col-md-4 d-flex align-items-center">
                                                <span className="text-muted small">
                                                    Mostrando {aceites.length > 0 ? (pageIndex * pageSize + 1) : 0} até {Math.min((pageIndex + 1) * pageSize, totalAceites)} de {totalAceites} registros
                                                </span>
                                            </div>
                                            <div className="col-md-4 d-flex justify-content-center align-items-center">
                                                <div className="btn-group" role="group">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => previousPage()}
                                                        disabled={!canPreviousPage || loadingAceites}
                                                    >
                                                        <i className="bi bi-chevron-left"></i> Anterior
                                                    </Button>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        disabled
                                                        style={{ cursor: 'default', backgroundColor: 'white' }}
                                                    >
                                                        Página {pageIndex + 1} de {pageOptions.length || 1}
                                                    </Button>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => nextPage()}
                                                        disabled={!canNextPage || loadingAceites}
                                                    >
                                                        Próxima <i className="bi bi-chevron-right"></i>
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="col-md-4 d-flex justify-content-end align-items-center">
                                                <Form.Label className="mb-0 me-2 small text-muted">Itens por página:</Form.Label>
                                                <Form.Select
                                                    value={pageSize}
                                                    onChange={e => {
                                                        setPageSize(Number(e.target.value));
                                                    }}
                                                    style={{ width: '80px' }}
                                                    size="sm"
                                                >
                                                    {[10, 20, 30, 50, 100].map(size => (
                                                        <option key={size} value={size}>
                                                            {size}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            </div>

            {/* Modal de Upload */}
            <Modal show={showModalUpload} onHide={() => {
                setShowModalUpload(false);
                reset();
                setFileSelected(false);
            }} size="lg" backdrop="static">
                <Form onSubmit={handleSubmit(onSubmitUpload)}>
                    <Modal.Header closeButton>
                        <span className="fw-semibold fs-5 text-primary">Trocar Termo de Aceite</span>
                    </Modal.Header>
                    <Modal.Body className="bg-gray-100 p-4">
                        <Form.Group className="mb-3">
                            <Form.Label>Selecionar Arquivo PDF (máximo 10MB)</Form.Label>
                            <label
                                className={`form-control label-input ${fileSelected ? 'border-success' : 'border-primary'} cursor-pointer`}
                                style={{ minHeight: '50px', display: 'flex', alignItems: 'center' }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <i className="bi bi-file-earmark-pdf-fill me-2"></i>
                                {fileSelected ? 'Arquivo selecionado' : 'Selecionar arquivo PDF'}
                            </label>
                            <Controller
                                control={control}
                                name="arquivo"
                                rules={{ required: 'Selecione um arquivo PDF' }}
                                render={({ field: { onChange, onBlur, ref } }) => (
                                    <input
                                        type="file"
                                        style={{ display: 'none' }}
                                        accept=".pdf"
                                        onBlur={onBlur}
                                        ref={(e) => {
                                            ref(e);
                                            fileInputRef.current = e;
                                        }}
                                        onChange={(e) => {
                                            const files = e.target.files;
                                            onChange(files);
                                            setFileSelected(files && files.length > 0);
                                        }}
                                    />
                                )}
                            />
                            {errors.arquivo && (
                                <Form.Text className="text-danger">{errors.arquivo.message}</Form.Text>
                            )}
                        </Form.Group>
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle me-2"></i>
                            Ao fazer upload de um novo termo, o termo anterior será desativado automaticamente, mas permanecerá no histórico.
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-end">
                        <div className="d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn me-2"
                                onClick={() => {
                                    setShowModalUpload(false);
                                    reset();
                                    setFileSelected(false);
                                }}
                                disabled={uploading}
                            >
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={uploading}>
                                {uploading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-upload me-2"></i>
                                        Fazer Upload
                                    </>
                                )}
                            </button>
                        </div>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de Visualização */}
            <Modal show={showModalVisualizar} onHide={() => setShowModalVisualizar(false)} size="xl">
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Visualizar Termo</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    {termoVisualizar && (
                        <div className="border rounded p-3">
                            <iframe
                                src={getFileUrl(termoVisualizar.arquivo)}
                                title="Visualizar Termo"
                                style={{ width: '100%', height: '600px', border: 'none' }}
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn btn-primary" onClick={() => setShowModalVisualizar(false)}>
                            Fechar
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default Termos;

